import { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { useDropzone } from 'react-dropzone'
import UploadOutlinedIcon from '@mui/icons-material/UploadOutlined'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import CallMergeIcon from '@mui/icons-material/CallMerge'
import DifferenceOutlinedIcon from '@mui/icons-material/DifferenceOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import CloseIcon from '@mui/icons-material/Close'
import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { api } from '../api/client.js'
import { errorMessage, errorCode } from '../api/errors.js'
import { downloadFile } from '../api/downloads.js'
import { streamOperation } from '../api/operations.js'
import { exportAnnotated, importAnnotated } from '../api/redactionResults.js'
import { tokens } from '../theme.js'
import { useFileSelection } from '../hooks/useFileSelection.js'
import { ProjectHeader, ProjectColumns, ProjectMain, SortSelect, SortLabel, ProjectControls, ActiveViewChip } from './styles/ProjectPageStyles.js'
import { AppShell } from '../components/layout/AppShell.js'
import {
  PageTitle,
  Button,
  SecondaryButton,
  MutedText,
  MedBodyText,
  InlineMessage,
  IconOnlyButton,
  Breadcrumb,
  whenLabel,
  plural
} from '../components/styles/index.js'
import { childFolders, filesIn, breadcrumb, folderMeta, selectableRows, collapseFolderSelection, subtreeIds } from '../components/files/directory.js'
import { EMPTY_FILTER, displayedFiles, nextSort, sortLabel, SORT_COLUMNS } from '../components/files/tableView.js'
import { pdfLike, convertible } from '../components/files/fileStatus.js'
import { FilterControl } from '../components/files/FilterPanel.js'
import {
  NameDialog,
  ConfirmDeleteDialog,
  ExcludeDialog,
  DialogPanel,
  MoveToFolderDialog,
  BulkResultDialog,
  skipReason
} from '../components/common/dialogs.js'
import { ORDER_ERRORS, operationFailure } from '../components/common/errorCopy.js'
import { UploadDialog } from '../components/workflows/UploadDialog.js'
import { DedupDialog } from '../components/workflows/DedupDialog.js'
import { PrepareDialog } from '../components/workflows/PrepareDialog.js'
import { OperationCard } from '../components/workflows/OperationCard.js'
import { FolderTree, moveTargets, folderPath } from '../components/files/FolderTree.js'
import { FileTable } from '../components/files/FileTable.js'
import { SelectionBar } from '../components/files/SelectionBar.js'

const colors = tokens.colors
const fontFamily = tokens.fonts.body

const RUNNING_LABEL = { convert: 'Converting…', combine: 'Combining…' }
const failedNames = (op, names) => (op.result?.failed ?? []).map(f => names[f.fileId]).filter(Boolean)
const DEFAULT_PRIORITY = ['filename', 'document', 'metadata']
const preventNativeChildDrag = event => {
  if (event.target.closest?.('a, img')) event.preventDefault()
}
const SORT_KEYS = new Set(SORT_COLUMNS.map(column => column.key))
const PROJECT_HISTORY = new Map()
const parseSort = params => {
  const key = params.get('sort')
  const dir = params.get('dir')
  return SORT_KEYS.has(key) && (dir === 'asc' || dir === 'desc') ? { key, dir } : null
}
const parseFilter = params => ({
  name: params.get('q') || '',
  kinds: (params.get('kind') || '').split(',').filter(Boolean),
  statuses: (params.get('status') || '').split(',').filter(Boolean)
})

function Notice({ tone, text, action, onClose, testId }) {
  return (
    <InlineMessage
      tone={tone}
      style={{ marginBottom: 12, alignItems: 'center', padding: '10px 20px', ...(tone === 'info' ? { background: `${colors.primary}12` } : {}) }}
      data-testid={testId}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {action && (
          <Button type="button" padding="short" onClick={action.onClick} data-testid={action.testId}>
            {action.icon}
            {action.label}
          </Button>
        )}
        <span style={{ flex: 1 }}>{text}</span>
        <IconOnlyButton
          type="button"
          onClick={onClose}
          title="Dismiss"
          aria-label="Dismiss"
          style={{ padding: 2, alignSelf: 'center' }}
          data-testid={`${testId}-close`}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconOnlyButton>
      </div>
    </InlineMessage>
  )
}

export default function ProjectPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const location = useLocation()
  const selectionRef = useRef(null)
  const mainRef = useRef(null)
  const [project, setProject] = useState(null)
  const [loaded, setLoaded] = useState(false) // the first listing (files and folders) has arrived
  const [files, setFiles] = useState([]) // every shown file of the project: the root's in the root's order, then the rest
  const [excluded, setExcluded] = useState([]) // the project's excluded files, latest first
  const [folderFiles, setFolderFiles] = useState({}) // folder id -> its files in that folder's order
  const [folders, setFolders] = useState([])
  const [priority, setPriority] = useState(DEFAULT_PRIORITY)
  const [running, setRunning] = useState({}) // fileId -> label while an SSE-tracked operation runs
  const [dialog, setDialog] = useState(null) // { kind, folder?, files? }
  const [error, setError] = useState('')
  const [orderError, setOrderError] = useState('')
  const [notice, setNotice] = useState('')
  const [sort, setSortState] = useState(() => parseSort(params)) // the column the view is sorted by, or the container's order
  const [filter, setFilterState] = useState(() => parseFilter(params))
  const [prepare, setPrepare] = useState(null) // the running preparation: form + operation tree
  const [prepared, setPrepared] = useState(null) // the finished one, until dismissed

  const viewParamKey = params.toString()
  const restoreHistoryKey = location.state?.restoreHistoryKey
  const historyState = PROJECT_HISTORY.get(restoreHistoryKey || location.key) || {}
  const saveHistoryState = useCallback(
    (patch = {}) => {
      const previous = PROJECT_HISTORY.get(location.key) || {}
      PROJECT_HISTORY.set(location.key, {
        selection: patch.selection ?? [...(selectionRef.current?.selectedIds || [])],
        collapsed: patch.collapsed ?? previous.collapsed ?? [],
        scrollTop: patch.scrollTop ?? mainRef.current?.scrollTop ?? 0
      })
    },
    [location.key]
  )
  useEffect(() => () => saveHistoryState(), [location.key, saveHistoryState])
  useEffect(() => {
    const nextSort = parseSort(params)
    const nextFilter = parseFilter(params)
    setSortState(previous => (JSON.stringify(previous) === JSON.stringify(nextSort) ? previous : nextSort))
    setFilterState(previous => (JSON.stringify(previous) === JSON.stringify(nextFilter) ? previous : nextFilter))
  }, [viewParamKey])
  const setSort = nextValue => {
    saveHistoryState()
    const next = typeof nextValue === 'function' ? nextValue(sort) : nextValue
    setSortState(next)
    setParams(previous => {
      const nextParams = new URLSearchParams(previous)
      if (next) {
        nextParams.set('sort', next.key)
        nextParams.set('dir', next.dir)
      } else {
        nextParams.delete('sort')
        nextParams.delete('dir')
      }
      return nextParams
    })
  }
  const setFilter = nextValue => {
    saveHistoryState()
    const raw = { ...EMPTY_FILTER, ...(nextValue || {}) }
    const next = {
      name: String(raw.name || ''),
      kinds: Array.isArray(raw.kinds) ? raw.kinds : [],
      statuses: Array.isArray(raw.statuses) ? raw.statuses : []
    }
    setFilterState(next)
    setParams(previous => {
      const nextParams = new URLSearchParams(previous)
      if (next.name.trim()) nextParams.set('q', next.name)
      else nextParams.delete('q')
      if (next.kinds.length) nextParams.set('kind', next.kinds.join(','))
      else nextParams.delete('kind')
      if (next.statuses.length) nextParams.set('status', next.statuses.join(','))
      else nextParams.delete('status')
      return nextParams
    })
  }
  const activeId = params.get('view') === 'excluded' ? 'excluded' : params.get('folder') || 'all'
  const setActiveId = useCallback(
    next => {
      saveHistoryState()
      setParams(previous => {
        const nextParams = new URLSearchParams(previous)
        nextParams.delete('folder')
        nextParams.delete('view')
        if (next === 'excluded') nextParams.set('view', 'excluded')
        else if (next && next !== 'all') nextParams.set('folder', next)
        return nextParams
      })
    },
    [saveHistoryState, setParams]
  )
  const excludedView = activeId === 'excluded'
  const activeFolderId = activeId === 'all' || excludedView ? null : activeId
  useEffect(() => {
    const state = PROJECT_HISTORY.get(restoreHistoryKey || location.key)
    const frame = requestAnimationFrame(() => {
      if (mainRef.current) mainRef.current.scrollTop = state?.scrollTop || 0
    })
    return () => cancelAnimationFrame(frame)
  }, [location.key, activeId, restoreHistoryKey])
  const loadFolders = useCallback(async () => {
    const { data } = await api.get(`/api/projects/${id}/folders`)
    setFolders(data)
  }, [id])
  const loadFiles = useCallback(async () => {
    const all = api.get(`/api/projects/${id}/files`)
    const hidden = api.get(`/api/projects/${id}/files`, { params: { view: 'excluded' } })
    const inFolder = activeFolderId ? api.get(`/api/projects/${id}/files`, { params: { folderId: activeFolderId } }) : null
    const listed = loadFolders().catch(() => {})
    const allData = (await all).data
    const hiddenData = (await hidden).data
    setFiles(allData)
    setExcluded(hiddenData)
    let folderData = null
    if (inFolder) {
      folderData = (await inFolder).data
      setFolderFiles(m => ({ ...m, [activeFolderId]: folderData }))
    }
    await listed
    setLoaded(true)
    return { files: allData, excluded: hiddenData, folderFiles: folderData }
  }, [id, activeFolderId, loadFolders])
  const loadProject = useCallback(() => api.get(`/api/projects/${id}`).then(r => setProject(r.data)), [id])
  useEffect(() => {
    loadProject().catch(() => navigate('/', { replace: true }))
    api
      .get('/api/settings/fileOrdering')
      .then(r => setPriority(r.data.priority))
      .catch(() => {})
  }, [id, loadProject, navigate])
  useEffect(() => {
    loadFiles().catch(() => {})
  }, [loadFiles])
  useEffect(() => {
    if (activeFolderId && folders.length && !folders.some(g => g.id === activeFolderId)) setParams({}, { replace: true })
  }, [activeFolderId, folders, setParams])
  useEffect(() => {
    setNotice('')
    setPrepared(null)
  }, [activeId])
  useEffect(() => {
    if (!notice) return undefined
    const t = setTimeout(() => setNotice(''), 8000)
    return () => clearTimeout(t)
  }, [notice])

  const fail = e => setError(errorMessage(e))

  const { getRootProps, isDragActive } = useDropzone({
    onDrop: accepted => accepted.length && setDialog({ kind: 'upload', seed: accepted }),
    noClick: true,
    noKeyboard: true,
    useFsAccessApi: false
  })
  const onUploaded = ({ files: f, folders: g }) => {
    if (f) loadFiles().catch(() => {})
    if (g) loadFolders().catch(() => {})
  }

  const importInput = useRef(null)
  const [importing, setImporting] = useState(null) // the row a Replace was picked for
  async function exportRow(f) {
    setRunning(r => ({ ...r, [f.id]: 'Writing annotated PDF…' }))
    try {
      await exportAnnotated(f)
    } catch (e) {
      fail(e)
    }
    setRunning(r => {
      const n = { ...r }
      delete n[f.id]
      return n
    })
    await loadFiles().catch(() => {})
  }
  function importRow(f) {
    setImporting(f)
    importInput.current?.click()
  }
  async function importChosen(e) {
    const chosen = e.target.files?.[0]
    e.target.value = ''
    const f = importing
    setImporting(null)
    if (!chosen || !f) return
    setRunning(r => ({ ...r, [f.id]: 'Reading annotated PDF…' }))
    try {
      const res = await importAnnotated(f.id, chosen, f.updatedAt)
      if (res.stale) setError(`${f.name} changed since this list was loaded. The boxes were not replaced. Reload and try again.`)
      else
        setNotice(
          `Replaced the boxes of ${f.name} from ${chosen.name}: ${res.done.boxes === 1 ? '1 box' : `${res.done.boxes} boxes`} now${res.done.skipped ? `, ${res.done.skipped} skipped (no area on the page)` : ''}.`
        )
    } catch (err) {
      fail(err)
    }
    setRunning(r => {
      const n = { ...r }
      delete n[f.id]
      return n
    })
    await loadFiles().catch(() => {})
  }
  function finish(verb, total, result) {
    if (!result.skipped.length) {
      selection.clear()
      return setDialog(null)
    }
    const nameOf = fid => files.find(f => f.id === fid)?.name || fid
    setDialog({
      kind: 'bulk-result',
      title: `${result[verb].length} of ${plural(total, 'file')} ${verb}`,
      skipped: result.skipped.map(s => ({ id: s.id, name: nameOf(s.id), reason: skipReason(s.code) }))
    })
  }
  async function bulkAction(path, verb, fileIds, extra) {
    try {
      const { data } = await api.post(`/api/projects/${id}/files/${path}`, { fileIds, ...extra })
      finish(verb, fileIds.length, data)
    } catch (e) {
      setDialog(null)
      fail(e)
    }
    await loadFiles().catch(() => {})
  }
  const idsOf = list => list.map(f => f.id)
  const deleteFiles = list => bulkAction('delete', 'deleted', idsOf(list))
  const moveFiles = (fileIds, folderId) => bulkAction('move', 'moved', fileIds, { folderId })
  const excludeFiles = (list, note) => bulkAction('exclude', 'excluded', idsOf(list), { note })
  const restoreFiles = list => bulkAction('restore', 'restored', idsOf(list))
  function onDuplicatesExcluded(result) {
    setDialog(null)
    const n = result.excluded.length
    setNotice(`Excluded ${plural(n, 'file')} · listed under Excluded, where ${n === 1 ? 'it' : 'they'} can be restored.`)
    loadFiles().catch(() => {})
  }
  async function setExclusionNote(f, note) {
    const { data } = await api.patch(`/api/files/${f.id}/exclusion`, { note })
    setExcluded(list => list.map(x => (x.id === f.id ? { ...x, ...data } : x)))
  }
  async function renameFile(f, name) {
    const { data } = await api.patch(`/api/files/${f.id}`, { name })
    const patch = list => list.map(x => (x.id === f.id ? { ...x, ...data } : x))
    setFiles(patch)
    if (activeFolderId) setFolderFiles(m => ({ ...m, [activeFolderId]: patch(m[activeFolderId] || []) }))
  }

  const orderPath = activeFolderId ? `/api/folders/${activeFolderId}/order` : `/api/projects/${id}/order`
  function applyContainer(data) {
    if (activeFolderId) setFolders(list => list.map(g => (g.id === data.id ? { ...g, ...data } : g)))
    else setProject(p => ({ ...p, ...data }))
  }
  async function reorder(fileIds) {
    const baseVersion = (activeFolderId ? folders.find(g => g.id === activeFolderId) : project)?.orderVersion ?? null
    try {
      const { data } = await api.patch(orderPath, { fileIds, baseVersion })
      applyContainer(data)
      setOrderError('')
    } catch (e) {
      const code = errorCode(e)
      if (ORDER_ERRORS[code]) {
        setOrderError(ORDER_ERRORS[code])
        await Promise.all([loadFolders(), loadProject()]).catch(() => {})
      } else fail(e)
    }
    await loadFiles().catch(() => {})
  }
  async function setOrderMode(mode) {
    try {
      const { data } = mode === 'manual' ? await api.patch(orderPath, { fileIds: container.map(f => f.id) }) : await api.delete(orderPath)
      applyContainer(data)
      setOrderError('')
    } catch (e) {
      fail(e)
    }
    await loadFiles().catch(() => {})
  }

  async function runOperation(kind, fileIds) {
    const label = RUNNING_LABEL[kind]
    try {
      const { data } = await api.post(`/api/projects/${id}/${kind}`, { fileIds })
      setRunning(r => ({ ...r, ...Object.fromEntries(fileIds.map(fid => [fid, label])) }))
      await streamOperation(data.operationId, {
        onStatus: op => {
          if (op.status === 'DONE' || op.status === 'ERROR') {
            setRunning(r => {
              const n = { ...r }
              for (const fid of fileIds) delete n[fid]
              return n
            })
            if (op.status === 'ERROR') setError(operationFailure(kind, failedNames(op, names)))
            loadFiles().catch(() => {})
          }
        }
      })
    } catch (e) {
      setRunning(r => {
        const n = { ...r }
        for (const fid of fileIds) delete n[fid]
        return n
      })
      fail(e)
      await loadFiles().catch(() => {})
    }
  }

  async function followPrepare(data, form) {
    setDialog(null)
    selection.clear()
    const startedAt = Date.now()
    const ids = data.combinedFileId ? [...form.fileIds, data.combinedFileId] : form.fileIds
    const mark = busy =>
      setRunning(r => {
        const n = { ...r }
        for (const fid of ids) delete n[fid]
        for (const fid of busy) n[fid] = 'Preparing…'
        return n
      })
    mark(form.fileIds)
    setPrepare({ ...form, operationId: data.operationId, startedAt, op: null })
    const sync = op => {
      setPrepare(p => (p ? { ...p, op } : p))
      const busy = new Set()
      for (const ch of op.children ?? []) if (ch.status === 'RUNNING') for (const fid of ch.input?.fileIds ?? []) busy.add(fid)
      mark(busy)
      loadFiles().catch(() => {})
    }
    try {
      const op = await streamOperation(data.operationId, { onStatus: sync })
      if (op.status === 'DONE') {
        setPrepared({
          count: form.fileIds.length,
          combined: !!op.result?.combinedFileId,
          suggested: op.result?.suggested ?? 0,
          seconds: Math.max(1, Math.round((Date.now() - startedAt) / 1000)),
          openId: op.result?.combinedFileId || form.fileIds[0]
        })
      } else {
        setError(operationFailure('prepare', failedNames(op, names)))
      }
    } catch (e) {
      fail(e)
    }
    setPrepare(null)
    mark([])
    await loadFiles().catch(() => {})
  }

  async function createFolder(name) {
    await api.post(`/api/projects/${id}/folders`, { name, parentId: dialog.parentId || undefined })
    setDialog(null)
    await loadFolders()
  }
  async function renameFolder(name) {
    await api.patch(`/api/folders/${dialog.folder.id}`, { name })
    setDialog(null)
    await loadFolders()
  }
  async function moveFolder(parentId) {
    await moveFolderIds([dialog.folder.id], parentId || null)
    setDialog(null)
  }
  async function moveFolderIds(folderIds, parentId) {
    const roots = collapseFolderSelection(folders, folderIds)
    const failures = []
    for (const folderId of roots) {
      try {
        await api.patch(`/api/folders/${folderId}`, { parentId: parentId || null })
      } catch (e) {
        failures.push(e)
      }
    }
    if (failures.length) fail(failures[0])
    await loadFolders()
    return failures
  }
  const canDropFolder = (folderIds, targetId) => !targetId || folderIds.every(folderId => !subtreeIds(folders, folderId).has(targetId))
  const folderMoveTargets = folderIds => folders.filter(folder => canDropFolder(folderIds, folder.id))
  async function moveSelection(folderIds, fileIds, parentId) {
    setDialog(null)
    const failures = []
    if (fileIds.length) {
      try {
        const { data } = await api.post(`/api/projects/${id}/files/move`, { fileIds, folderId: parentId || null })
        if (data.skipped?.length) failures.push(...data.skipped)
      } catch (e) {
        failures.push(e)
      }
    }
    failures.push(...(await moveFolderIds(folderIds, parentId || null)))
    if (failures.length) setError(failures[0]?.message || 'Some items could not be moved.')
    else selection.clear()
    await loadFiles().catch(() => {})
  }
  function requestUncombine(file) {
    setDialog({ kind: 'uncombine', file })
  }
  async function uncombineFile() {
    try {
      await api.post(`/api/files/${dialog.file.id}/uncombine`)
      setDialog(null)
      await Promise.all([loadFiles(), loadFolders()])
      setNotice('Combined document removed. Original files are visible again.')
    } catch (e) {
      fail(e)
    }
  }
  async function deleteFolder() {
    try {
      await api.delete(`/api/folders/${dialog.folder.id}`)
      if (activeId === dialog.folder.id) setActiveId('all')
    } catch (e) {
      fail(e)
    }
    setDialog(null)
    await Promise.all([loadFolders(), loadFiles()])
  }

  async function requestDeleteFolder(folder) {
    const snapshot = await loadFiles().catch(() => null)
    setDialog({
      kind: 'delete-folder',
      folder,
      previewFiles: [...(snapshot?.files || []), ...(snapshot?.excluded || []), ...(snapshot?.folderFiles || [])]
    })
  }

  async function deleteSelection() {
    const folderIds = (dialog.folders || []).map(folder => (typeof folder === 'string' ? folder : folder?.id)).filter(Boolean)
    const roots = collapseFolderSelection(folders, folderIds)
    const covered = new Set(roots.flatMap(folderId => [...subtreeIds(folders, folderId)]))
    const fileIds = (dialog.files || []).filter(f => !covered.has(f.folderId)).map(f => f.id)
    const failures = []
    for (const folderId of roots) {
      try {
        await api.delete(`/api/folders/${folderId}`)
      } catch (e) {
        failures.push(e)
      }
    }
    if (fileIds.length) {
      try {
        const { data } = await api.post(`/api/projects/${id}/files/delete`, { fileIds })
        if (data.skipped?.length) failures.push(...data.skipped)
      } catch (e) {
        failures.push(e)
      }
    }
    setDialog(null)
    await Promise.all([loadFolders(), loadFiles()])
    if (failures.length) setError(failures[0]?.message || 'Some items could not be deleted.')
    else selection.clear()
  }

  const container = activeFolderId ? folderFiles[activeFolderId] || filesIn(files, activeFolderId) : filesIn(files, null)
  const subfolders = excludedView ? [] : childFolders(folders, activeFolderId)
  const displayed = displayedFiles(container, { sort, filter }, running)
  const hiddenCount = container.length - displayed.length
  const shown = excludedView ? excluded : displayed
  const selection = useFileSelection({
    files: selectableRows({ loaded, excludedView, shown, folders }),
    resetKey: `${activeId}:${location.key}`,
    initialIds: historyState.selection || []
  })
  selectionRef.current = selection
  const selectedFiles = shown.filter(f => selection.isSelected(f.id))
  const selectedFolders = excludedView ? [] : folders.filter(g => selection.isSelected(g.id))
  const selectedIds = [...selectedFiles.map(f => f.id), ...selectedFolders.map(g => g.id)]
  const selectedRaw = selectedFiles.filter(convertible)
  const selectedPdfs = selectedFiles.filter(pdfLike)
  const activeFolder = folders.find(g => g.id === activeId)
  const orderManual = !!(activeFolder ? activeFolder.orderManual : project?.orderManual)
  const openFile = (f, artifact = f.artifact) => {
    const target = new URLSearchParams()
    if (artifact) target.set('artifact', artifact)
    const from = `${location.pathname}${location.search}`
    if (from) target.set('from', from)
    const query = target.toString()
    navigate(`/p/${id}/files/${f.id || f.fileId}${query ? `?${query}` : ''}`, { state: { fromHistoryKey: location.key } })
  }
  const pathed = list => list.map(g => ({ id: g.id, name: folderPath(folders, g.id) }))
  const names = Object.fromEntries(files.map(f => [f.id, f.name]))
  const containerLabel = activeFolder?.name || 'All files'
  const crumbs = breadcrumb(folders, project?.name || '…', activeFolderId)
  const counts = activeFolder
    ? {
        subfolderCount: activeFolder.subfolderCount ?? 0,
        fileCount: container.length,
        totalFileCount: activeFolder.totalFileCount ?? container.length
      }
    : { subfolderCount: subfolders.length, fileCount: container.length, totalFileCount: files.length }
  const countLabel = counts.subfolderCount
    ? `${plural(counts.subfolderCount, 'folder')} · ${plural(counts.fileCount, 'file')} here`
    : plural(counts.fileCount, 'file')

  async function requestDeleteSelection() {
    const selectedFileSnapshot = selectedFiles
    const selectedFolderIds = selectedFolders.map(folder => folder.id)
    const snapshot = await loadFiles().catch(() => null)
    setDialog({
      kind: 'delete-selection',
      files: selectedFileSnapshot,
      folders: selectedFolderIds,
      previewFiles: [...(snapshot?.files || []), ...(snapshot?.excluded || []), ...(snapshot?.folderFiles || [])]
    })
  }

  const actions = excludedView
    ? [{ label: 'Restore', testId: 'restore-selected-btn', primary: true, onClick: () => restoreFiles(selectedFiles) }]
    : [
        {
          label: 'Convert',
          icon: <PictureAsPdfOutlinedIcon />,
          testId: 'convert-btn',
          disabled: selectedFolders.length > 0 || selectedRaw.length === 0,
          onClick: () => {
            const ids = selectedRaw.map(f => f.id)
            selection.clear()
            runOperation('convert', ids)
          }
        },
        {
          label: 'Find duplicates',
          icon: <DifferenceOutlinedIcon />,
          testId: 'find-duplicates-selected-btn',
          disabled: selectedFolders.length > 0 || selectedFiles.length < 2,
          onClick: () => setDialog({ kind: 'dedup', files: selectedFiles })
        },
        {
          label: 'Combine',
          icon: <CallMergeIcon />,
          testId: 'combine-btn',
          disabled: selectedFolders.length > 0 || selectedPdfs.length === 0,
          onClick: () => {
            const ids = selectedPdfs.map(f => f.id)
            selection.clear()
            runOperation('combine', ids)
          }
        },
        {
          label: 'Prepare for redaction',
          icon: <AutoFixHighOutlinedIcon />,
          testId: 'prepare-btn',
          primary: true,
          disabled: selectedFolders.length > 0 || !!prepare,
          onClick: () => setDialog({ kind: 'prepare', files: selectedFiles })
        },
        {
          label: 'Download',
          icon: <DownloadOutlinedIcon />,
          testId: 'download-selected-btn',
          dividerBefore: true,
          disabled: selectedFolders.length > 0 || selectedFiles.length === 0,
          onClick: () => selectedFiles.forEach(f => downloadFile(f))
        },
        {
          label: 'Move to folder',
          icon: <DriveFileMoveOutlinedIcon />,
          testId: 'move-selected-btn',
          onClick: () => setDialog({ kind: 'move-selection', files: selectedFiles, folders: selectedFolders })
        },
        { label: 'Delete', icon: <DeleteOutlineIcon />, testId: 'delete-selected-btn', danger: true, onClick: requestDeleteSelection }
      ]

  const deleteDialogFolderIds =
    dialog?.kind === 'delete-selection'
      ? (dialog.folders || []).map(folder => (typeof folder === 'string' ? folder : folder?.id)).filter(Boolean)
      : []
  const deleteDialogRoots = dialog?.kind === 'delete-selection' ? collapseFolderSelection(folders, deleteDialogFolderIds) : []
  const deleteDialogCovered = new Set(deleteDialogRoots.flatMap(folderId => [...subtreeIds(folders, folderId)]))
  const knownFiles = [
    ...new Map(
      [...files, ...excluded, ...Object.values(folderFiles).flat(), ...(dialog?.previewFiles || []), ...(dialog?.files || [])].map(file => [
        file.id,
        file
      ])
    ).values()
  ]
  const deleteDialogFolders = deleteDialogRoots.flatMap(folderId => folders.filter(folder => subtreeIds(folders, folderId).has(folder.id)))
  const deleteDialogFiles =
    dialog?.kind === 'delete-selection'
      ? knownFiles.filter(
          file =>
            deleteDialogCovered.has(file.folderId) ||
            (dialog.files || []).some(chosen => chosen.id === file.id && !deleteDialogCovered.has(chosen.folderId))
        )
      : []
  const deleteDialogCount = deleteDialogRoots.length + deleteDialogFiles.filter(file => !deleteDialogCovered.has(file.folderId)).length

  const directDeleteFolderId = dialog?.kind === 'delete-folder' ? dialog.folder?.id : null
  const directDeleteCovered = directDeleteFolderId ? subtreeIds(folders, directDeleteFolderId) : new Set()
  const directDeleteFolders = directDeleteFolderId ? folders.filter(folder => directDeleteCovered.has(folder.id)) : []
  const directDeleteFiles = directDeleteFolderId ? knownFiles.filter(file => directDeleteCovered.has(file.folderId)) : []
  const directDeleteSourceCount = directDeleteFolderId
    ? new Set(directDeleteFiles.filter(file => file.kind === 'COMBINED').flatMap(file => file.sources || [])).size
    : 0
  const attachmentsOf = (list, { excludedToo = true } = {}) => {
    const named = new Set((list || []).map(f => f.id))
    return knownFiles.filter(f => f.derivedFromFileId && named.has(f.derivedFromFileId) && !named.has(f.id) && (excludedToo || !f.excludedAt))
  }
  const attachmentNote = attachments => {
    const count = attachments.length
    const emails = new Set(attachments.map(f => f.derivedFromFileId)).size
    return count
      ? ` The ${count === 1 ? 'attachment' : `${count} attachments`} converted from ${emails === 1 ? 'the email' : 'the emails'} ${count === 1 ? 'is' : 'are'} deleted with ${emails === 1 ? 'it' : 'them'}.`
      : ''
  }
  const deleteAttachments = dialog?.kind === 'delete-files' ? attachmentsOf(dialog.files) : []
  const deleteSelectionAttachments = dialog?.kind === 'delete-selection' ? attachmentsOf(deleteDialogFiles) : []
  const deleteCombinedSourceCount =
    dialog?.kind === 'delete-files'
      ? new Set((dialog.files || []).filter(file => file.kind === 'COMBINED').flatMap(file => file.sources || [])).size
      : 0
  const deleteSelectionSourceCount =
    dialog?.kind === 'delete-selection'
      ? new Set((dialog.files || []).filter(file => file.kind === 'COMBINED').flatMap(file => file.sources || [])).size
      : 0

  const title = project?.name || '…'
  return (
    <AppShell
      fill
      crumbs={[
        { label: 'Projects', to: '/', testId: 'crumb-projects' },
        { label: title, testId: 'crumb-project' }
      ]}>
      <ProjectHeader>
        <div style={{ minWidth: 0 }}>
          <PageTitle data-testid="project-title" style={{ fontSize: 22 }}>
            {title}
          </PageTitle>
          <MutedText style={{ marginTop: 2 }} data-testid="project-meta">
            {plural(files.length, 'file')} · {plural(folders.length, 'folder')}
            {excluded.length ? ` · ${excluded.length} excluded` : ''}
            {project ? ` · last activity ${whenLabel(project.updatedAt)}` : ''}
          </MutedText>
        </div>
        <div style={{ display: 'flex', gap: 12, flex: '0 0 auto', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <SecondaryButton type="button" onClick={() => setDialog({ kind: 'dedup' })} data-testid="find-duplicates-btn">
            <DifferenceOutlinedIcon /> Find duplicates…
          </SecondaryButton>
          <Button onClick={() => setDialog({ kind: 'upload' })} data-testid="upload-btn">
            <UploadOutlinedIcon /> Upload
          </Button>
        </div>
      </ProjectHeader>

      <ProjectColumns>
        <FolderTree
          folders={folders}
          files={files}
          excludedCount={excluded.length}
          activeId={activeId}
          onSelect={setActiveId}
          selection={selection}
          historyKey={location.key}
          initialCollapsed={historyState.collapsed || []}
          onCollapsedChange={collapsed => saveHistoryState({ collapsed })}
          onCreate={parentId => setDialog({ kind: 'new-folder', parentId })}
          onRename={folder => setDialog({ kind: 'rename-folder', folder })}
          onMove={folder => setDialog({ kind: 'move-folder', folder })}
          onDelete={requestDeleteFolder}
          onDropFiles={moveFiles}
          onDropFolders={moveFolderIds}
        />

        <ProjectMain ref={mainRef} {...getRootProps()} onDragStart={preventNativeChildDrag} $drag={isDragActive} data-testid="drop-zone">
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 24, marginBottom: 16 }}>
            <div style={{ minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
              {excludedView ? (
                <span style={{ font: `600 18px/1.3 ${fontFamily}`, color: colors.text.heading }} data-testid="table-title">
                  Excluded
                </span>
              ) : (
                <Breadcrumb items={crumbs} onSelect={gid => setActiveId(gid || 'all')} currentTestId="table-title" data-testid="folder-crumbs" />
              )}
              <MutedText style={excludedView ? undefined : { marginTop: 4, width: '100%' }} data-testid="table-meta">
                {excludedView
                  ? `${plural(shown.length, 'file')} · hidden from the table and from Prepare for redaction · kept in storage`
                  : folderMeta(counts)}
              </MutedText>
            </div>

            {!excludedView && container.length > 0 && (
              <ProjectControls>
                <SortLabel
                  style={sort ? { opacity: 0.45 } : undefined}
                  title={sort ? `The view is sorted by ${sortLabel(sort)}. Clear it to order ${containerLabel} again.` : undefined}>
                  <MutedText>Order:</MutedText>
                  <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                    <ExpandMoreIcon style={{ position: 'absolute', left: 8, pointerEvents: 'none', fontSize: 18, color: `${colors.text.body}BF` }} />
                    <SortSelect
                      value={orderManual ? 'manual' : 'effective'}
                      onChange={e => setOrderMode(e.target.value)}
                      disabled={!!sort}
                      data-testid="order-select"
                      aria-label="Order"
                      data-disabled={!!sort}>
                      <option value="effective">Document date</option>
                      <option value="manual">Manual</option>
                    </SortSelect>
                  </span>
                </SortLabel>
                {sort && (
                  <ActiveViewChip data-testid="sort-chip" data-sort={`${sort.key}:${sort.dir}`}>
                    {sort.dir === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                    View: sorted by {sortLabel(sort)}
                    <span aria-hidden="true">·</span>
                    <button type="button" onClick={() => setSort(null)} data-testid="sort-clear">
                      clear
                    </button>
                  </ActiveViewChip>
                )}
                <FilterControl files={container} filter={filter} onChange={setFilter} shownCount={displayed.length} />
              </ProjectControls>
            )}
          </div>
          {error && (
            <MedBodyText style={{ color: colors.error, marginBottom: 12 }} data-testid="project-error">
              {error}
            </MedBodyText>
          )}
          {orderError && <Notice tone="info" text={orderError} onClose={() => setOrderError('')} testId="order-error" />}
          {notice && <Notice tone="success" text={notice} onClose={() => setNotice('')} testId="project-notice" />}
          {prepared && (
            <Notice
              tone="success"
              testId="prepare-notice"
              onClose={() => setPrepared(null)}
              action={{
                label: 'Open annotated PDF',
                icon: <OpenInNewIcon />,
                testId: 'open-prepared',
                onClick: () => openFile({ id: prepared.openId })
              }}
              text={`Prepared ${plural(prepared.count, 'file')} in ${prepared.seconds} s. ${prepared.combined ? '1 combined document, ' : prepared.count > 1 ? `${plural(prepared.count, 'suggested set')}, ` : ''}${prepared.suggested} suggested ${prepared.suggested === 1 ? 'box' : 'boxes'}.`}
            />
          )}
          {prepare && <OperationCard prepare={prepare} names={names} />}

          <SelectionBar count={selectedIds.length} actions={actions} />
          <input ref={importInput} type="file" accept="application/pdf,.pdf" hidden onChange={importChosen} data-testid="import-input" />

          <FileTable
            files={shown}
            selection={selection}
            running={running}
            resetKey={activeId}
            view={excludedView ? 'excluded' : 'files'}
            onNote={setExclusionNote}
            onOpen={openFile}
            onDownload={downloadFile}
            onConvert={f => runOperation('convert', [f.id])}
            onRename={renameFile}
            onMove={f => setDialog({ kind: 'move-files', files: [f] })}
            onExclude={f => setDialog({ kind: 'exclude-files', files: [f] })}
            onRestore={f => restoreFiles([f])}
            onDelete={f => setDialog({ kind: 'delete-files', files: [f] })}
            onUncombine={requestUncombine}
            onExportAnnotated={exportRow}
            onImportAnnotated={importRow}
            onReorder={reorder}
            orderManual={orderManual}
            priority={priority}
            sort={excludedView ? null : sort}
            onSort={key => setSort(s => nextSort(s, key))}
            hiddenCount={excludedView ? 0 : hiddenCount}
            containerLabel={activeFolder ? 'this folder' : 'All files'}
            countLabel={countLabel}
            folders={subfolders}
            onOpenFolder={setActiveId}
            onRenameFolder={folder => setDialog({ kind: 'rename-folder', folder })}
            onMoveFolder={folder => setDialog({ kind: 'move-folder', folder })}
            onDeleteFolder={requestDeleteFolder}
            onDropOnFolder={moveFiles}
            onDropFolder={moveFolderIds}
            canDropFolder={canDropFolder}
          />
          {shown.length === 0 && subfolders.length === 0 && (
            <MutedText style={{ padding: '32px 0', textAlign: 'center' }} data-testid="files-empty">
              {excludedView
                ? "No excluded files. Files you exclude from Find duplicates or a row's menu appear here and can be restored."
                : hiddenCount
                  ? `No file here matches the filter. It hides all ${plural(hiddenCount, 'file')}.`
                  : isDragActive
                    ? 'Drop to upload'
                    : `No files ${activeFolder ? 'in this folder' : 'yet'}. Drop files here or use Upload.`}
            </MutedText>
          )}
        </ProjectMain>
      </ProjectColumns>

      <UploadDialog
        open={dialog?.kind === 'upload'}
        projectId={id}
        projectName={project?.name}
        folders={folders}
        folderId={activeFolderId}
        seed={dialog?.seed}
        onChanged={onUploaded}
        onClose={() => setDialog(null)}
      />
      <DedupDialog
        open={dialog?.kind === 'dedup'}
        projectId={id}
        files={files}
        folders={folders}
        activeFolderId={activeFolderId}
        selection={dialog?.kind === 'dedup' ? (dialog.files ?? []) : []}
        onClose={() => setDialog(null)}
        onApplied={onDuplicatesExcluded}
      />
      {dialog?.kind === 'prepare' && (
        <PrepareDialog
          open
          projectId={id}
          files={dialog.files}
          containerLabel={containerLabel}
          onClose={() => setDialog(null)}
          onStarted={followPrepare}
        />
      )}
      {dialog?.kind === 'exclude-files' && (
        <ExcludeDialog
          files={dialog.files}
          attachments={attachmentsOf(dialog.files, { excludedToo: false }).length}
          onConfirm={note => excludeFiles(dialog.files, note)}
          onClose={() => setDialog(null)}
        />
      )}
      <NameDialog
        open={dialog?.kind === 'new-folder'}
        title={dialog?.parentId ? 'New subfolder' : 'New folder'}
        label="Folder name"
        confirmLabel="Create folder"
        confirmTestId="folder-confirm"
        inputTestId="folder-name-input"
        onConfirm={createFolder}
        onClose={() => setDialog(null)}
      />
      <NameDialog
        open={dialog?.kind === 'rename-folder'}
        title="Rename folder"
        label="Folder name"
        initial={dialog?.folder?.name || ''}
        confirmLabel="Rename"
        confirmTestId="folder-confirm"
        inputTestId="folder-name-input"
        onConfirm={renameFolder}
        onClose={() => setDialog(null)}
      />
      {dialog?.kind === 'move-folder' && (
        <MoveToFolderDialog
          title={`Move “${dialog.folder.name}”`}
          targets={pathed(moveTargets(folders, dialog.folder))}
          initial={dialog.folder.parentId || ''}
          onConfirm={moveFolder}
          onClose={() => setDialog(null)}
        />
      )}
      {(dialog?.kind === 'move-files' || dialog?.kind === 'move-selection') && (
        <MoveToFolderDialog
          title={
            dialog.kind === 'move-selection'
              ? `Move ${plural((dialog.files?.length || 0) + (dialog.folders?.length || 0), 'item')}`
              : `Move ${plural(dialog.files.length, 'file')}`
          }
          targets={pathed(dialog.kind === 'move-selection' ? folderMoveTargets((dialog.folders || []).map(g => g.id)) : folders)}
          initial={(() => {
            const targets = dialog.kind === 'move-selection' ? folderMoveTargets((dialog.folders || []).map(g => g.id)) : folders
            return targets.some(target => target.id === activeFolderId) ? activeFolderId : ''
          })()}
          onConfirm={folderId =>
            dialog.kind === 'move-selection'
              ? moveSelection(
                  (dialog.folders || []).map(g => g.id),
                  (dialog.files || []).map(f => f.id),
                  folderId
                )
              : moveFiles(
                  dialog.files.map(f => f.id),
                  folderId
                )
          }
          onClose={() => setDialog(null)}
        />
      )}
      <ConfirmDeleteDialog
        open={dialog?.kind === 'uncombine'}
        title="Uncombine this document?"
        confirmLabel="Uncombine"
        confirmIcon={<CallMergeIcon />}
        onConfirm={uncombineFile}
        onClose={() => setDialog(null)}>
        Redactions and exports on the combined document will be deleted. Original files and their existing redactions will be kept.
      </ConfirmDeleteDialog>
      <ConfirmDeleteDialog
        open={dialog?.kind === 'delete-folder'}
        title="Delete folder permanently?"
        confirmLabel="Delete folder"
        confirmIcon={<DeleteOutlineIcon />}
        onConfirm={deleteFolder}
        onClose={() => setDialog(null)}>
        <span data-testid="delete-folder-dialog">
          {directDeleteSourceCount > 0
            ? `“${dialog?.folder?.name}”, its subfolders, files, and the ${plural(directDeleteSourceCount, 'source file')} tied to any combined document inside are removed from storage, including nested sources. This includes each PDF, redaction box, and any redacted, annotated, or summary PDF. This cannot be undone.`
            : `“${dialog?.folder?.name}”, its subfolders, and the ${plural(dialog?.folder?.totalFileCount ?? 0, 'file')} inside are removed from storage. This includes each PDF, redaction box, and any redacted, annotated, or summary PDF. This cannot be undone.`}
        </span>
        <DialogPanel data-testid="delete-folder-list" style={{ maxHeight: 240, overflowY: 'auto' }}>
          {directDeleteFolders.map(folder => (
            <div key={folder.id}>
              <FolderOutlinedIcon sx={{ fontSize: 16, color: colors.sectionDivider }} />
              <span>{folderPath(folders, folder.id)} · folder</span>
            </div>
          ))}
          {directDeleteFiles.map(file => (
            <div key={file.id}>
              <DescriptionOutlinedIcon sx={{ fontSize: 16, color: colors.sectionDivider }} />
              <span>
                {file.name}
                {file.status === 'REDACTED' && <span style={{ color: colors.text.muted }}> · redacted</span>}
              </span>
            </div>
          ))}
          {!directDeleteFolders.length && !directDeleteFiles.length && <MutedText>No items found in this folder.</MutedText>}
        </DialogPanel>
      </ConfirmDeleteDialog>
      <ConfirmDeleteDialog
        open={dialog?.kind === 'delete-files'}
        title={`Delete ${plural(dialog?.files?.length ?? 0, 'file')} permanently?`}
        confirmLabel={`Delete ${plural(dialog?.files?.length ?? 0, 'file')}`}
        confirmIcon={<DeleteOutlineIcon />}
        secondaryAction={{
          label: 'Exclude instead…',
          testId: 'exclude-instead',
          onClick: () => setDialog({ kind: 'exclude-files', files: dialog.files })
        }}
        onConfirm={() => deleteFiles(dialog.files)}
        onClose={() => setDialog(null)}>
        {deleteCombinedSourceCount > 0 ? (
          <span data-testid="delete-combined-warning">
            This permanently removes the combined document and its {plural(deleteCombinedSourceCount, 'source file')} tied to it, including nested
            sources, their PDFs, redaction boxes, and generated artifacts. This cannot be undone.
          </span>
        ) : dialog?.files?.length === 1 ? (
          'The file, its PDF, its redaction boxes and any redacted, annotated or summary PDF made from it are removed from storage. This cannot be undone. To keep it recoverable, use Exclude instead.'
        ) : (
          'The files, their PDFs, their redaction boxes and any redacted, annotated or summary PDFs made from them are removed from storage. This cannot be undone. To keep them recoverable, use Exclude instead.'
        )}
        {deleteAttachments.length > 0 && <span data-testid="delete-attachments-note">{attachmentNote(deleteAttachments)}</span>}
        <DialogPanel data-testid="delete-list">
          {(dialog?.files || []).map(f => (
            <div key={f.id}>
              <DescriptionOutlinedIcon sx={{ fontSize: 16, color: colors.sectionDivider }} />
              <span>
                {f.name}
                {f.status === 'REDACTED' && <span style={{ color: colors.text.muted }}> · redacted</span>}
              </span>
            </div>
          ))}
        </DialogPanel>
      </ConfirmDeleteDialog>
      <ConfirmDeleteDialog
        open={dialog?.kind === 'delete-selection'}
        title={`Delete ${plural(deleteDialogCount, 'item')} permanently?`}
        confirmLabel="Delete permanently"
        confirmIcon={<DeleteOutlineIcon />}
        onConfirm={deleteSelection}
        onClose={() => setDialog(null)}>
        <p>
          {deleteSelectionSourceCount > 0
            ? `Selected folders include their subfolders and files. Every selected item and the ${plural(deleteSelectionSourceCount, 'source file')} tied to a selected combined document will be removed with its stored PDFs, redaction boxes and generated artifacts. This cannot be undone.`
            : 'Selected folders include their subfolders and files. Every selected item and its stored PDFs, redaction boxes and generated artifacts will be removed. This cannot be undone.'}
          {deleteSelectionAttachments.length > 0 && <span data-testid="delete-attachments-note">{attachmentNote(deleteSelectionAttachments)}</span>}
        </p>
        <DialogPanel data-testid="delete-selection-list" style={{ maxHeight: 240, overflowY: 'auto' }}>
          {deleteDialogFolders.map(folder => (
            <div key={folder.id}>
              <FolderOutlinedIcon sx={{ fontSize: 16, color: colors.sectionDivider }} />
              <span>{folderPath(folders, folder.id)} · folder</span>
            </div>
          ))}
          {deleteDialogFiles.map(file => (
            <div key={file.id}>
              <DescriptionOutlinedIcon sx={{ fontSize: 16, color: colors.sectionDivider }} />
              <span>
                {file.name}
                {file.status === 'REDACTED' && <span style={{ color: colors.text.muted }}> · redacted</span>}
              </span>
            </div>
          ))}
          {!deleteDialogFolders.length && !deleteDialogFiles.length && <MutedText>No items selected.</MutedText>}
        </DialogPanel>
      </ConfirmDeleteDialog>
      {dialog?.kind === 'bulk-result' && <BulkResultDialog title={dialog.title} skipped={dialog.skipped} onClose={() => setDialog(null)} />}
    </AppShell>
  )
}
